/**
 * 命令式 API:createDateBar / createBookBar / createPackageList(B2)
 *
 * 各创建对应自定义元素并挂载到容器,把回调桥接到 DOM 事件。
 * 组件内部由 React 渲染并派发 book/datechange 事件(composed:true 穿 Shadow DOM),
 * 此处的 addEventListener 即接收;setDate 经 check-in/check-out 属性驱动 React 状态。
 */
import type {
  CreateDateBarOptions,
  CreateBookBarOptions,
  CreatePackageListOptions,
  DateBarHandle,
  BookPayload,
  DateChangePayload,
} from '../types';

/** 三元素命令式参数的公共字段(供 createImpl 共用) */
interface CreateImplOptions {
  el: string | HTMLElement;
  checkIn?: string;
  checkOut?: string;
  /** 内联列表房型数量上限(仅 hotel-package-list 生效,其余元素忽略);正整数生效,不传=默认 3 个 */
  limit?: number;
  onBook?: (pkg: BookPayload) => void;
  onDateChange?: (checkIn: string, checkOut: string) => void;
}

function resolveEl(el: string | HTMLElement): HTMLElement {
  if (typeof el === 'string') {
    const node = document.querySelector(el);
    if (!node) {
      throw new Error(`[HotelSDK] 未找到容器选择器 "${el}"`);
    }
    return node as HTMLElement;
  }
  return el;
}

/** 桥接 book/datechange 事件到回调(组件派发的 DOM 事件 composed:true 穿 Shadow DOM) */
function bridgeEvents(
  comp: HTMLElement,
  onBook: CreateImplOptions['onBook'],
  onDateChange: CreateImplOptions['onDateChange']
): void {
  if (onBook) {
    comp.addEventListener('book', (e) => {
      onBook?.((e as CustomEvent<BookPayload>).detail);
    });
  }
  if (onDateChange) {
    comp.addEventListener('datechange', (e) => {
      const detail = (e as CustomEvent<DateChangePayload>).detail;
      if (detail) onDateChange?.(detail.checkIn, detail.checkOut);
    });
  }
}

/** 共用实现:建元素 -> 设日期属性 -> 桥接事件 -> 挂载 -> 返回句柄 */
function createImpl(tagName: string, opts: CreateImplOptions): DateBarHandle {
  const container = resolveEl(opts.el);
  const comp = document.createElement(tagName);
  if (opts.checkIn) comp.setAttribute('check-in', opts.checkIn);
  if (opts.checkOut) comp.setAttribute('check-out', opts.checkOut);
  // limit 仅对 hotel-package-list 有意义;设在 date-bar/book-bar 上会被忽略。不传/非正则不设属性,组件用默认 3 个
  if (opts.limit && opts.limit > 0) comp.setAttribute('limit', String(opts.limit));
  bridgeEvents(comp, opts.onBook, opts.onDateChange);
  container.appendChild(comp);
  return {
    setDate(checkIn: string, checkOut: string) {
      comp.setAttribute('check-in', checkIn);
      comp.setAttribute('check-out', checkOut);
    },
    destroy() {
      comp.remove();
    },
  };
}

/** 命令式创建纯日期栏 <hotel-date-bar>(仅 onDateChange) */
export function createDateBar(opts: CreateDateBarOptions): DateBarHandle {
  return createImpl('hotel-date-bar', opts);
}

/** 命令式创建 <hotel-book-bar>(onBook + onDateChange) */
export function createBookBar(opts: CreateBookBarOptions): DateBarHandle {
  return createImpl('hotel-book-bar', opts);
}

/** 命令式创建 <hotel-package-list>(onBook + onDateChange) */
export function createPackageList(opts: CreatePackageListOptions): DateBarHandle {
  return createImpl('hotel-package-list', opts);
}
